'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCommerceSource,
  useBlockPart,
  tx,
  PortalLink,
  type BrandCard,
} from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, SectionShell, type PaddingBox } from '@akademi/site-builder-blocks';

type Localized = string | Record<string, string>;

/**
 * The brands a shop stocks, as a strip of logo plates.
 *
 * <p>A trade buyer shops by brand as often as by category — they arrived wanting a Sunsynk
 * inverter, not "an inverter" — so naming the brands carried is both navigation and evidence
 * that the range is real.
 *
 * <p><strong>Where a logo is missing the plate sets the brand's NAME.</strong> The feeds carry
 * no artwork, and a manufacturer's logo is their trade mark: it cannot be invented, traced or
 * pulled off their site. A wordmark plate is honest about what it is, reads as a logo slot,
 * and needs no permission. {@link BrandCard.logoUrl} is used the moment it is populated, so
 * uploading artwork later replaces these with nothing to change here.
 *
 * <p>Each plate searches for the brand by name rather than linking a brand facet: the facet
 * exists but does not filter (`?f_brand=aiko` returns the whole catalogue), and a tile that
 * silently shows everything is worse than one that shows a few too many.
 */
export interface BrandStripProps {
  title?: Localized;
  /** How many plates to show, biggest ranges first. Default 12. */
  limit?: number;
  /** Show each brand's product count under its name. Default true. */
  showCount?: boolean;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_LIMIT = 12;

export function BrandStrip({
  title,
  limit = DEFAULT_LIMIT,
  showCount = true,
  paddingOuter,
  paddingInner,
}: BrandStripProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const [brands, setBrands] = useState<BrandCard[] | null>(null);

  useEffect(() => {
    if (!commerce) return;
    let cancelled = false;
    commerce
      .listBrands()
      .then((rows) => {
        if (!cancelled) setBrands(rows);
      })
      .catch(() => {
        if (!cancelled) setBrands([]);
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (!commerce) {
    return (
      <SectionShell title={tx(title)} paddingOuter={paddingOuter} paddingInner={paddingInner}>
        <AdapterPlaceholder
          blockType="BrandStrip"
          adapterName="CommerceSource"
          reason={t('commerce.adapter.reason')}
        />
      </SectionShell>
    );
  }

  // Ranked by how much of the range each brand actually accounts for. A strip led by whoever
  // happens to sort first alphabetically tells a buyer nothing about what this shop is for.
  const shown = (brands ?? [])
    .filter((brand) => (brand.productCount ?? 0) > 0)
    .sort((a, b) => (b.productCount ?? 0) - (a.productCount ?? 0))
    .slice(0, Math.max(1, Math.trunc(limit)));

  // Nothing at all rather than a heading over an empty rail, on a shop with no brands yet.
  if (brands !== null && shown.length === 0) return null;

  return (
    <SectionShell title={tx(title)} paddingOuter={paddingOuter} paddingInner={paddingInner}>
      <ul style={gridStyle} {...part('list')}>
        {shown.map((brand) => (
          <li key={brand.id} style={cellStyle}>
            <PortalLink
              href={`/search?q=${encodeURIComponent(brand.name)}`}
              style={plateStyle}
              aria-label={brand.name}
            >
              {brand.logoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element -- per-tenant asset host.
                <img src={brand.logoUrl} alt={brand.name} style={logoStyle} loading="lazy" />
              ) : (
                <span style={wordmarkStyle}>{brand.name}</span>
              )}
              {showCount && brand.productCount ? (
                <span style={countStyle}>
                  {t('commerce.grid.resultCount', { count: brand.productCount })}
                </span>
              ) : null}
            </PortalLink>
          </li>
        ))}
      </ul>
    </SectionShell>
  );
}

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))',
  gap: 12,
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

const cellStyle: CSSProperties = { display: 'flex' };

const plateStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  minHeight: 88,
  padding: '18px 14px',
  border: '1px solid var(--color-border-subtle, #e2e8f0)',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-card, #fff)',
  color: 'inherit',
  textDecoration: 'none',
  textAlign: 'center',
};

const logoStyle: CSSProperties = {
  maxWidth: '100%',
  maxHeight: 40,
  objectFit: 'contain',
  display: 'block',
};

/** The stand-in for artwork: the brand set as a plate, not a line of body copy. */
const wordmarkStyle: CSSProperties = {
  fontSize: 15,
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
  color: 'var(--color-text-primary, #0f172a)',
};

const countStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--color-text-muted, #71717a)',
};
